import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FaCar, FaUserTie, FaMoneyBillWave, FaHistory, FaSync, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import axios from 'axios';
import { ENDPOINT } from '../api/auth';
import { useAuth } from '../context/AuthContext';
import './CarwashTransaction.css';

const CarwashTransaction = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    worker_ids: [],
    vehicle_id: null,
    wash_type: 'full',
    payment_method: null,
    bank_name: ''
  });
  
  // Data state
  const [workers, setWorkers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [transactionStatuses, setTransactionStatuses] = useState({});
  
  // Constants
  const paymentMethods = ['Cash', 'Institution', 'Bank'];
  const banks = ['CBE', 'Awash', 'Dashen', 'Abyssinia', 'Birhan', 'Telebirr', 'Check'];
  const washTypes = [
    { id: 'full', label: 'Full Wash' },
    { id: 'partial', label: 'Partial Wash' }
  ];

  // Fetch initial data
  useEffect(() => {
    fetchData();
    fetchDailyTransactions();
  }, []);

  // Fetch workers and vehicles
  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch workers
      const workersRes = await axios.get(ENDPOINT, {
        params: { action: 'get_unpaid_workers' },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (workersRes.data.success) {
        setWorkers(workersRes.data.workers || []);
      } else {
        throw new Error(workersRes.data.message || 'Failed to fetch workers');
      }
      
      // Fetch vehicles
      const vehiclesRes = await axios.get(ENDPOINT, {
        params: { action: 'get_vehicles' },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (vehiclesRes.data.success) {
        setVehicles(vehiclesRes.data.vehicles || []);
      } else {
        throw new Error(vehiclesRes.data.message || 'Failed to fetch vehicles');
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(error.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch daily transactions
  const fetchDailyTransactions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(ENDPOINT, {
        params: { action: 'get_daily_carwash_transactions' },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.data.success) {
        // Load saved statuses from localStorage
        const savedStatuses = JSON.parse(localStorage.getItem('transactionStatuses') || '{}');
        setTransactionStatuses(savedStatuses);

        // Merge with transactions
        const transactionsWithStatus = res.data.transactions.map(tx => ({
          ...tx,
          status: savedStatuses[tx.id] || 'not received'
        }));

        setTransactions(transactionsWithStatus);
      } else {
        throw new Error(res.data.message || 'Failed to fetch transactions');
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error(error.response?.data?.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Toggle worker selection
  const toggleWorker = (workerId) => {
    setFormData(prev => {
      const newWorkerIds = prev.worker_ids.includes(workerId)
        ? prev.worker_ids.filter(id => id !== workerId)
        : [...prev.worker_ids, workerId];
      
      return {
        ...prev,
        worker_ids: newWorkerIds
      };
    });
  };

  // Toggle transaction status
  const toggleTransactionStatus = async (transactionId) => {
    const newStatuses = { ...transactionStatuses };
    const currentStatus = newStatuses[transactionId] || 'not received';
    newStatuses[transactionId] = currentStatus === 'received' ? 'not received' : 'received';

    // Save to localStorage
    localStorage.setItem('transactionStatuses', JSON.stringify(newStatuses));
    
    // Update state
    setTransactionStatuses(newStatuses);
    setTransactions(prevTransactions =>
      prevTransactions.map(tx =>
        tx.id === transactionId 
          ? { ...tx, status: newStatuses[transactionId] } 
          : tx
      )
    );
  };

  // Handle payment of commission
  const handlePayCommission = async (workerId) => {
    if (!window.confirm('Are you sure you want to mark this commission as paid?')) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        ENDPOINT,
        { 
          action: 'pay_commission',
          worker_id: workerId 
        },
        { 
          headers: { 'Authorization': `Bearer ${token}` } 
        }
      );

      if (response.data.success) {
        toast.success('Commission paid successfully');
        fetchData(); // Refresh the list
      } else {
        throw new Error(response.data.message || 'Failed to pay commission');
      }
    } catch (error) {
      console.error('Error paying commission:', error);
      toast.error(error.response?.data?.message || 'Failed to process payment');
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (formData.worker_ids.length === 0) {
      toast.error('Please select at least one worker');
      return;
    }
    
    if (!formData.vehicle_id) {
      toast.error('Please select a vehicle');
      return;
    }
    
    if (!formData.payment_method) {
      toast.error('Please select a payment method');
      return;
    }
    
    if (formData.payment_method === 'Bank' && !formData.bank_name) {
      toast.error('Please select a bank');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        action: 'create_carwash_transaction',
        bank_name: formData.payment_method === 'Bank' ? formData.bank_name : ''
      };

      const response = await axios.post(
        ENDPOINT,
        payload,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Carwash transaction recorded successfully');
        
        // Reset form
        setFormData({
          worker_ids: [],
          vehicle_id: null,
          wash_type: 'full',
          payment_method: null,
          bank_name: ''
        });
        
        // Refresh data
        fetchData();
        fetchDailyTransactions();
      } else {
        throw new Error(response.data.message || 'Failed to record transaction');
      }
    } catch (error) {
      console.error('Error recording transaction:', error);
      toast.error(error.response?.data?.message || 'Failed to record transaction');
    } finally {
      setSaving(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",") + ' ETB';
  };

  // Format date
  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    return new Date(dateString).toLocaleString('en-US', options);
  };

  // Filter workers with unpaid commissions
  const unpaidWorkers = workers.filter(worker => parseFloat(worker.unpaid_commission || 0) > 0);

  if (loading && workers.length === 0 && vehicles.length === 0) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading data...</p>
      </div>
    );
  }

  return (
    <div className="carwash-transaction">
      <div className="page-header">
        <h1><FaCar className="header-icon" /> Carwash Transaction Management</h1>
        <p>Record new carwash transactions and manage commissions</p>
      </div>

      <div className="card form-card">
        <h2><FaCar /> Record New Wash</h2>
        
        <form onSubmit={handleSubmit} className="carwash-form">
          <div className="form-group">
            <label>Select Workers *</label>
            <div className="selection-container">
              {workers.map(worker => (
                <div 
                  key={`worker-${worker.id}`}
                  className={`selection-item ${formData.worker_ids.includes(worker.id) ? 'selected' : ''}`}
                  onClick={() => toggleWorker(worker.id)}
                >
                  <div className="selection-content">
                    <div className="selection-text">
                      {worker.name}
                      {worker.unpaid_commission > 0 && (
                        <span className="unpaid-badge">
                          Unpaid: {formatCurrency(worker.unpaid_commission)}
                        </span>
                      )}
                    </div>
                    {formData.worker_ids.includes(worker.id) && (
                      <div className="selection-check">
                        <FaCheckCircle />
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {workers.length === 0 && (
                <p className="no-items">No workers available</p>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Select Vehicle *</label>
            <div className="selection-container">
              {vehicles.map(vehicle => (
                <div 
                  key={`vehicle-${vehicle.id}`}
                  className={`selection-item ${formData.vehicle_id === vehicle.id ? 'selected' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, vehicle_id: vehicle.id }))}
                >
                  <div className="selection-content">
                    <div className="selection-text">
                      {vehicle.name}
                      <span className="tariff-badge">
                        {formatCurrency(vehicle.tariff)}
                      </span>
                    </div>
                    {formData.vehicle_id === vehicle.id && (
                      <div className="selection-check">
                        <FaCheckCircle />
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {vehicles.length === 0 && (
                <p className="no-items">No vehicles available</p>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Wash Type *</label>
            <div className="btn-group">
              {washTypes.map(type => (
                <button
                  key={type.id}
                  type="button"
                  className={`btn ${formData.wash_type === type.id ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => setFormData(prev => ({ ...prev, wash_type: type.id }))}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Payment Method *</label>
            <div className="btn-group">
              {paymentMethods.map(method => (
                <button
                  key={method}
                  type="button"
                  className={`btn ${formData.payment_method === method ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => setFormData(prev => ({ ...prev, payment_method: method, bank_name: '' }))}
                >
                  {method}
                </button>
              ))}
            </div>
          </div>

          {formData.payment_method === 'Bank' && (
            <div className="form-group">
              <label>Select Bank *</label>
              <div className="selection-container">
                {banks.map(bank => (
                  <div 
                    key={`bank-${bank}`}
                    className={`selection-item ${formData.bank_name === bank ? 'selected' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, bank_name: bank }))}
                  >
                    <div className="selection-content">
                      <div className="selection-text">{bank}</div>
                      {formData.bank_name === bank && (
                        <div className="selection-check">
                          <FaCheckCircle />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="form-actions">
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={saving || formData.worker_ids.length === 0 || !formData.vehicle_id || !formData.payment_method || (formData.payment_method === 'Bank' && !formData.bank_name)}
            >
              {saving ? 'Processing...' : 'Record Transaction'}
            </button>
          </div>
        </form>
      </div>

      <div className="card">
        <div className="card-header">
          <h2><FaMoneyBillWave /> Unpaid Commissions</h2>
          <button 
            className="btn btn-icon"
            onClick={fetchData}
            disabled={loading}
            title="Refresh"
          >
            <FaSync className={loading ? 'spinning' : ''} />
          </button>
        </div>
        
        {unpaidWorkers.length > 0 ? (
          <div className="unpaid-commissions">
            {unpaidWorkers.map(worker => (
              <div key={`unpaid-${worker.id}`} className="commission-item">
                <div className="commission-info">
                  <div className="worker-name">{worker.name}</div>
                  <div className="commission-amount">
                    {formatCurrency(worker.unpaid_commission)}
                  </div>
                </div>
                <button
                  className="btn btn-sm btn-success"
                  onClick={() => handlePayCommission(worker.id)}
                  disabled={loading}
                >
                  Mark as Paid
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <FaCheckCircle className="empty-icon" />
            <p>No unpaid commissions at the moment</p>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <h2><FaHistory /> Today's Transactions</h2>
          <button 
            className="btn btn-icon"
            onClick={fetchDailyTransactions}
            disabled={loading}
            title="Refresh"
          >
            <FaSync className={loading ? 'spinning' : ''} />
          </button>
        </div>
        
        {transactions.length > 0 ? (
          <div className="transactions-list">
            {transactions.map(tx => (
              <div key={`tx-${tx.id}`} className="transaction-item">
                <div className="transaction-header">
                  <div className="transaction-vehicle">
                    {tx.vehicle_type} - {tx.plate_number || 'N/A'}
                  </div>
                  <div className={`transaction-amount ${tx.status === 'received' ? 'received' : 'not-received'}`}>
                    {formatCurrency(tx.total_amount)}
                  </div>
                </div>
                
                <div className="transaction-details">
                  <div className="detail-row">
                    <span className="detail-label">Wash Type:</span>
                    <span className="detail-value">
                      {tx.wash_type ? (tx.wash_type.charAt(0).toUpperCase() + tx.wash_type.slice(1)) : 'N/A'}
                    </span>
                  </div>
                  
                  <div className="detail-row">
                    <span className="detail-label">Time:</span>
                    <span className="detail-value">
                      {formatDate(tx.transaction_date)}
                    </span>
                  </div>
                  
                  <div className="detail-row">
                    <span className="detail-label">Payment:</span>
                    <span className="detail-value">
                      {tx.payment_method}
                      {tx.bank_name && ` (${tx.bank_name})`}
                    </span>
                  </div>
                  
                  <div className="detail-row">
                    <span className="detail-label">Workers:</span>
                    <span className="detail-value">
                      {tx.worker_names ? tx.worker_names.split(',').join(', ') : 'N/A'}
                    </span>
                  </div>
                </div>
                
                <div className="transaction-actions">
                  <div className={`status-badge ${tx.status === 'received' ? 'status-received' : 'status-pending'}`}>
                    {tx.status === 'received' ? (
                      <><FaCheckCircle /> Received</>
                    ) : (
                      <><FaTimesCircle /> Not Received</>
                    )}
                  </div>
                  
                  <button
                    className={`btn btn-sm ${tx.status === 'received' ? 'btn-outline-warning' : 'btn-outline-success'}`}
                    onClick={() => toggleTransactionStatus(tx.id)}
                    disabled={loading}
                  >
                    {tx.status === 'received' ? 'Mark Not Received' : 'Mark as Received'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <FaHistory className="empty-icon" />
            <p>No transactions recorded today</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CarwashTransaction;
